import { useState, useCallback, useMemo } from "react";

export interface OrganizationFilters {
  search: string;
  status: string;
  paymentProvider: string;
  page: number;
  limit: number;
}

export const useOrganizationFilters = () => {
  const [filters, setFilters] = useState<OrganizationFilters>({
    search: "",
    status: "all",
    paymentProvider: "all",
    page: 1,
    limit: 25,
  });

  const handleFilterChange = useCallback(
    (key: keyof OrganizationFilters, value: string | number) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value,
        // Reset page when filters change (except when changing page itself)
        ...(key !== 'page' && { page: 1 }),
      }));
    },
    []
  );

  const resetFilters = useCallback(() => {
    setFilters({
      search: "",
      status: "all",
      paymentProvider: "all",
      page: 1,
      limit: 25,
    });
  }, []);

  // Memoize query params to prevent unnecessary re-renders
  const queryParams = useMemo(() => {
    const params: Record<string, any> = {
      page: filters.page,
      per_page: filters.limit,
    };
    
    // Add search parameter if not empty
    if (filters.search?.trim()) {
      params.search = filters.search.trim();
    }
    
    // Add status parameter if not "all"
    if (filters.status !== "all") {
      params.status = filters.status;
    }
    
    // Add payment_provider parameter - handle "none" case specially
    if (filters.paymentProvider !== "all") {
      if (filters.paymentProvider === "none") {
        params.payment_provider = "none"; // Let API handle this special case
      } else {
        params.payment_provider = filters.paymentProvider;
      }
    }
    
    return params;
  }, [filters.page, filters.limit, filters.search, filters.status, filters.paymentProvider]);

  return {
    filters,
    queryParams,
    handleFilterChange,
    resetFilters,
  };
}; 